import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * WebSocket 配置接口
 */
interface WebSocketConfig {
  url: string; // WebSocket 服务器的 URL
  onMessage: (event: MessageEvent) => void; // 接收到消息时的回调函数
  onOpen?: (event: Event) => void; // 连接成功建立时的回调函数
  onClose?: (event: CloseEvent) => void; // 连接关闭时的回调函数
  onError?: (event: Event) => void; // 发生错误时的回调函数
  reconnectInterval?: number; // 重连尝试的间隔时间（毫秒），默认为 3000ms
  reconnectAttempts?: number; // 最大重连尝试次数，默认为 5 次
}

/**
 * 用于管理 WebSocket 连接的 React Hook。
 * 提供连接状态、发送消息的方法，并实现断线重连逻辑。
 */
const useWebSocket = ({
  url, // WebSocket 服务器地址
  onMessage, // 接收到消息时的处理函数
  onOpen, // 连接打开时的处理函数
  onClose, // 连接关闭时的处理函数
  onError, // 连接出错时的处理函数
  reconnectInterval = 3000, // 默认重连间隔 3 秒
  reconnectAttempts = 5, // 默认重连尝试 5 次
}: WebSocketConfig) => {
  const callbacksRef = useRef({
    onMessage,
    onOpen,
    onClose,
    onError,
  });
  const reconnectConfigRef = useRef({
    reconnectInterval,
    reconnectAttempts,
  });
  // 使用 useRef 存储 WebSocket 实例，避免组件重新渲染时重复创建
  const ws = useRef<WebSocket | null>(null);
  const currentUrl = useRef<string>('');
  // 使用 useRef 存储重连定时器，以便在组件卸载或成功连接时清除
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
  // 连接状态，表示 WebSocket 是否已连接
  const [isConnected, setIsConnected] = useState(false);
  // 当前重连尝试次数，使用 useRef 保证最新值
  const attempt = useRef(0);
  // 使用 useRef 标记组件是否已卸载，防止在组件卸载后继续重连
  const isUnmounted = useRef(false);
  // 使用 useRef 标记是否正在连接中，防止重复连接
  const isConnecting = useRef(false);

  callbacksRef.current = {
    onMessage,
    onOpen,
    onClose,
    onError,
  };
  reconnectConfigRef.current = {
    reconnectInterval,
    reconnectAttempts,
  };

  /**
   * 建立 WebSocket 连接的函数。
   * 使用 useCallback 优化，确保在依赖项不变时函数引用不变。
   */
  const connect = useCallback(() => {
    if (!url || isUnmounted.current) {
      return;
    }

    // 防止重复连接：如果正在连接中或已连接，则跳过
    if (isConnecting.current) {
      return;
    }

    // 如果已经存在同一 URL 的 WebSocket 连接且状态正常，则跳过
    if (ws.current && (ws.current.readyState === WebSocket.CONNECTING || ws.current.readyState === WebSocket.OPEN)) {
      if (currentUrl.current === url) {
        return;
      }
      const staleWs = ws.current;
      ws.current = null;
      currentUrl.current = '';
      staleWs.close();
    }

    // 如果已经存在 WebSocket 连接，先关闭它
    if (ws.current) {
      ws.current.close();
      ws.current = null;
      currentUrl.current = '';
    }

    // 标记正在连接
    isConnecting.current = true;

    // 创建新的 WebSocket 实例
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
      ws.current = socket;
      currentUrl.current = url;
    } catch (error) {
      isConnecting.current = false;
      console.error('【wss】WebSocket 创建失败:', error);
      return;
    }

    // 监听连接打开事件
    socket.onopen = (event) => {
      if (ws.current !== socket) {
        return;
      }
      isConnecting.current = false; // 连接成功，重置连接标记
      setIsConnected(true); // 设置连接状态为 true
      attempt.current = 0; // 成功连接后，重置重连尝试次数
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      callbacksRef.current.onOpen?.(event); // 调用外部传入的 onOpen 回调
    };

    // 监听接收消息事件，直接调用外部传入的 onMessage 回调
    socket.onmessage = (event) => {
      if (ws.current !== socket) {
        return;
      }
      callbacksRef.current.onMessage(event);
    };

    // 监听连接关闭事件
    socket.onclose = (event) => {
      if (ws.current !== socket) {
        return;
      }
      isConnecting.current = false; // 连接关闭，重置连接标记
      ws.current = null;
      currentUrl.current = '';

      const closeInfo = {
        code: event.code,
        reason: event.reason || '无原因',
        wasClean: event.wasClean,
        attempt: attempt.current,
        timestamp: new Date().toISOString(),
      };

      // 针对 1006 错误码（异常关闭）提供更详细的诊断信息
      if (event.code === 1006) {
        const isLocalhost = typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            window.location.hostname.startsWith('192.168.') ||
            window.location.hostname.startsWith('10.') ||
            window.location.hostname.startsWith('172.'));

        if (isLocalhost) {
          console.error('【wss】WebSocket 异常关闭（错误码 1006）- 本地环境', {
            ...closeInfo,
            diagnosis: '连接在建立前被关闭，可能原因：\n' +
              '1. SSL/TLS 证书验证失败 - 本地开发环境常见问题\n' +
              '2. 服务器拒绝连接 - 检查服务器日志\n' +
              '3. 网络防火墙或代理阻止连接\n' +
              '4. Cookie 认证失败 - 检查 FDSID cookie 是否有效\n' +
              '建议：在浏览器控制台 Network 标签页查看 WebSocket 连接的详细信息',
          });
        } else {
          console.error('【wss】WebSocket 异常关闭（错误码 1006）', closeInfo);
        }
      }

      setIsConnected(false); // 设置连接状态为 false
      callbacksRef.current.onClose?.(event); // 调用外部传入的 onClose 回调
      const {
        reconnectAttempts: latestReconnectAttempts,
        reconnectInterval: latestReconnectInterval,
      } = reconnectConfigRef.current;

      // 如果重连尝试次数未达到上限，则尝试重连
      // 注意：1006 错误通常表示连接无法建立，可能需要检查网络或服务器配置
      if (attempt.current < latestReconnectAttempts && !isUnmounted.current) {
        const nextAttempt = attempt.current + 1;
        const delay = latestReconnectInterval * Math.pow(2, attempt.current); // 使用指数退避策略计算重连间隔

        reconnectTimeout.current = setTimeout(() => {
          // 检查组件是否已卸载
          if (isUnmounted.current) {
            return;
          }
          // 清除之前的连接引用
          ws.current = null;
          currentUrl.current = '';
          attempt.current = nextAttempt; // 更新重连尝试次数
          connect(); // 重新连接
        }, delay);
      } else if (attempt.current >= latestReconnectAttempts) {
        console.warn(`【wss】达到最大重连次数（${latestReconnectAttempts} 次），停止重连`);
      }
    };

    // 监听连接错误事件
    socket.onerror = (event) => {
      if (ws.current !== socket) {
        return;
      }
      // WebSocket error 事件对象通常不包含详细信息，我们需要从 WebSocket 实例获取状态信息
      const currentAttempt = attempt.current;
      const errorInfo = {
        url,
        readyState: socket.readyState,
        readyStateText: socket.readyState === WebSocket.CONNECTING ? 'CONNECTING' :
          socket.readyState === WebSocket.OPEN ? 'OPEN' :
            socket.readyState === WebSocket.CLOSING ? 'CLOSING' :
              socket.readyState === WebSocket.CLOSED ? 'CLOSED' : 'UNKNOWN',
        timestamp: new Date().toISOString(),
        attempt: currentAttempt + 1, // 当前尝试次数（从1开始计数，更符合用户理解）
        isReconnecting: currentAttempt > 0, // 是否为重连尝试
      };

      // 根据错误类型提供更详细的错误信息
      let errorMessage = 'WebSocket 连接发生错误';
      const isLocalhost = typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1' ||
          window.location.hostname.startsWith('192.168.') ||
          window.location.hostname.startsWith('10.') ||
          window.location.hostname.startsWith('172.'));

      if (socket.readyState === WebSocket.CONNECTING) {
        if (isLocalhost) {
          errorMessage = 'WebSocket 连接失败（本地环境）。可能的原因：\n' +
            '1. Cookie (FDSID) 未设置或已过期 - 请检查浏览器开发者工具的 Application > Cookies\n' +
            '2. SSL/TLS 证书问题 - 本地开发环境可能证书验证失败\n' +
            '3. 网络问题 - 检查是否能访问服务器\n' +
            '4. 环境变量 NEXT_PUBLIC_API_HOST 未正确配置';
        } else {
          errorMessage = 'WebSocket 连接失败，可能的原因：网络问题、服务器不可达、URL 无效或 SSL/TLS 证书问题';
        }
      } else if (socket.readyState === WebSocket.OPEN) {
        errorMessage = 'WebSocket 连接已建立但发生错误，可能是数据传输问题';
      }

      console.error('【wss】WebSocket 报错:', {
        ...errorInfo,
        message: errorMessage,
        originalEvent: event,
      });

      setIsConnected(false); // 设置连接状态为 false
      isConnecting.current = false; // 连接失败，重置连接标记
      callbacksRef.current.onError?.(event); // 调用外部传入的 onError 回调

      // 如果连接还在 CONNECTING 状态，手动关闭以触发 onclose 事件
      // 这样重连逻辑会在 onclose 中处理
      if (ws.current === socket && socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    };
  }, [url]);

  // useEffect 用于在组件挂载时建立连接，并在卸载时清理连接
  useEffect(() => {
    isUnmounted.current = false; // 组件挂载时重置标记

    if (url) {
      if (currentUrl.current !== url) {
        attempt.current = 0;
      }
      connect(); // 组件挂载时立即连接
    }

    return () => {
      // 组件卸载时设置标记
      isUnmounted.current = true;
      isConnecting.current = false; // 重置连接标记

      // 清除任何未完成的重连定时器
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }

      // 组件卸载时关闭 WebSocket 连接
      if (ws.current) {
        const currentWs = ws.current;
        if (ws.current === currentWs) {
          ws.current = null;
          currentUrl.current = '';
        }
        currentWs.close();
        setIsConnected(false);
      }
    };
  }, [connect, url]); // 依赖项为 connect 函数，当 connect 变化时（通常不会）重新执行

  /**
   * 发送消息的函数。
   * 使用 useCallback 优化，确保在依赖项不变时函数引用不变。
   */
  const sendMessage = useCallback((message: string | object) => {
    // 只有当 WebSocket 连接打开时才发送消息
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      // 如果消息是对象，则转换为 JSON 字符串再发送
      ws.current.send(typeof message === 'string' ? message : JSON.stringify(message));
    } else {
      console.warn('【wss】WebSocket 连接未打开，无法发送消息'); // 连接未打开，无法发送消息
    }
  }, []);

  // 返回连接状态和发送消息的函数
  return { isConnected, sendMessage };
};

export default useWebSocket;
